import { useState } from 'react';

import { TagPicker } from '@/components/contacts/TagPicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useUpdateContact } from '@/hooks/useContacts';

import type { Contact } from '@repo/shared-types';

const FIELD_CLASS =
  'focus:ring-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none';

/**
 * Rendered inside DialogContent, which Radix unmounts on close — so state
 * re-initializes from the contact on every open without reset effects.
 */
const EditContactForm = ({ contact, onClose }: { contact: Contact; onClose: () => void }) => {
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  // Tags are stored in the top-level contact.tags column.
  const [tags, setTags] = useState<string[]>(() => {
    const initial = contact.tags;
    return Array.isArray(initial) ? initial.filter((t) => typeof t === 'string') : [];
  });
  const [dataJson, setDataJson] = useState(() =>
    contact.data ? JSON.stringify(contact.data, null, 2) : '',
  );
  const [dataError, setDataError] = useState<string | null>(null);
  const updateMutation = useUpdateContact();
  const { toast } = useToast();

  /** Empty → null; otherwise must parse to a JSON object (not array/scalar). */
  const parseDataField = (): { ok: boolean; value: Record<string, unknown> | null } => {
    const trimmed = dataJson.trim();
    if (!trimmed) return { ok: true, value: null };
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setDataError('Data must be a JSON object, e.g. { "key": "value" }');
        return { ok: false, value: null };
      }
      setDataError(null);
      return { ok: true, value: parsed as Record<string, unknown> };
    } catch {
      setDataError('Invalid JSON — check for missing quotes, commas, or braces.');
      return { ok: false, value: null };
    }
  };

  const handleSave = async () => {
    const parsed = parseDataField();
    if (!parsed.ok) return;
    try {
      await updateMutation.mutateAsync({
        id: contact.id,
        data: {
          name,
          phone: phone || null,
          email: email || null,
          data: parsed.value,
          tags,
        },
      });
      onClose();
      toast({ variant: 'success', title: 'Contact updated' });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update contact. Check the phone/email format.',
      });
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">Name</label>
          <input className={FIELD_CLASS} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">Phone</label>
          <input className={FIELD_CLASS} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">Email</label>
          <input className={FIELD_CLASS} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Tags <span className="font-normal">(one per type — a new tag replaces its type)</span>
          </label>
          <TagPicker value={tags} onChange={setTags} />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Data <span className="font-normal">(JSON)</span>
          </label>
          <textarea
            className={`${FIELD_CLASS} min-h-28 resize-y font-mono text-xs leading-relaxed`}
            placeholder={'{\n  "key": "value"\n}'}
            value={dataJson}
            onChange={(e) => {
              setDataJson(e.target.value);
              if (dataError) setDataError(null);
            }}
            spellCheck={false}
          />
          {dataError && <p className="text-destructive mt-1 text-xs">{dataError}</p>}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={updateMutation.isPending || name.trim().length < 2}>
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
};

export const EditContactDialog = ({
  contact,
  open,
  onOpenChange,
}: {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit contact</DialogTitle>
      </DialogHeader>
      <EditContactForm contact={contact} onClose={() => onOpenChange(false)} />
    </DialogContent>
  </Dialog>
);
